import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, rupee, imageUrl } from "../lib/api";
import { useStores } from "../lib/stores";
import {
  Plus, Printer, Search, AlertTriangle, Pencil, Upload,
  ChevronDown, ChevronUp, ChevronLeft, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import BulkBarcodePrintModal from "../components/BulkBarcodePrintModal";
import BulkProductImportModal from "../components/BulkProductImportModal";
import ProductImageGalleryDialog from "../components/ProductImageGalleryDialog";

const SIZE_ORDER = ["Free Size", "XS", "S", "M", "L", "XL", "XXL", "XXXL"];

const ROW_GRID =
  "grid grid-cols-[36px_88px_56px_minmax(110px,1fr)_72px_96px_52px_48px_52px_88px] items-center gap-x-3 px-4";

const COLOR_PILLS = {
  maroon: { background: "#7f1d1d", color: "#fff" },
  red: { background: "#dc2626", color: "#fff" },
  blue: { background: "#1d4ed8", color: "#fff" },
  green: { background: "#15803d", color: "#fff" },
  black: { background: "#171717", color: "#fff" },
  white: { background: "#f5f5f5", color: "#171717", border: "1px solid var(--border-soft)" },
  gold: { background: "#b45309", color: "#fff" },
  pink: { background: "#db2777", color: "#fff" },
  purple: { background: "#7e22ce", color: "#fff" },
  orange: { background: "#ea580c", color: "#fff" },
  yellow: { background: "#ca8a04", color: "#fff" },
  cream: { background: "#fef3c7", color: "#78350f" },
  beige: { background: "#f5f0e8", color: "#57534e" },
};

function sizeRank(size) {
  const i = SIZE_ORDER.indexOf(size || "");
  return i === -1 ? 99 : i;
}

function pickProductImage(variants) {
  for (const p of variants) {
    if (Array.isArray(p.template_images) && p.template_images[0]) return p.template_images[0];
  }
  for (const p of variants) {
    if (Array.isArray(p.images) && p.images[0]) return p.images[0];
  }
  return null;
}

function pickVariantImage(p) {
  if (Array.isArray(p.images) && p.images[0]) return p.images[0];
  if (Array.isArray(p.template_images) && p.template_images[0]) return p.template_images[0];
  return null;
}

function collectProductImages(variants) {
  const seen = new Set();
  const out = [];
  const add = (url) => {
    if (!url || seen.has(url)) return;
    seen.add(url);
    out.push(url);
  };
  for (const p of variants) {
    if (Array.isArray(p.template_images)) {
      for (const img of p.template_images) add(img);
    }
  }
  for (const p of variants) {
    if (Array.isArray(p.images)) {
      for (const img of p.images) add(img);
    }
  }
  return out;
}

function groupProducts(items) {
  const byName = new Map();
  for (const p of items) {
    const key = p.name || "—";
    if (!byName.has(key)) {
      byName.set(key, { name: key, category: p.category, fabric: p.fabric, colors: new Map() });
    }
    const g = byName.get(key);
    const color = (p.color || "").trim() || "—";
    if (!g.colors.has(color)) g.colors.set(color, []);
    g.colors.get(color).push(p);
  }
  return [...byName.values()].map((g) => {
    const colorGroups = [...g.colors.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([color, variants]) => ({
        color,
        variants: [...variants].sort((a, b) => sizeRank(a.size) - sizeRank(b.size)),
      }));
    const allVariants = colorGroups.flatMap((cg) => cg.variants);
    const colors = colorGroups.map((cg) => cg.color).filter((c) => c !== "—");
    const sizes = [...new Set(allVariants.map((v) => v.size).filter(Boolean))].sort(
      (a, b) => sizeRank(a) - sizeRank(b)
    );
    return {
      name: g.name,
      category: g.category,
      fabric: g.fabric,
      shelf: allVariants[0]?.shelf_location || "",
      colorGroups,
      colors,
      sizes,
      variants: allVariants,
      variantCount: allVariants.length,
      image: pickProductImage(allVariants),
      images: collectProductImages(allVariants),
    };
  });
}

function colorPillStyle(name) {
  const key = (name || "").toLowerCase().replace(/\s+/g, "");
  return COLOR_PILLS[key] || { background: "var(--surface-muted)", color: "var(--text-main)" };
}

function ColorPill({ color }) {
  const label = color || "No color";
  const pill = color ? colorPillStyle(color) : { background: "var(--surface-muted)", color: "var(--text-muted)" };
  return (
    <span className="inline-flex items-center text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap" style={pill}>
      {label}
    </span>
  );
}

export default function Products() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [lowStock, setLowStock] = useState(false);
  const [bulkPrintItems, setBulkPrintItems] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [showImport, setShowImport] = useState(false);
  const [preview, setPreview] = useState(null);
  const [collapsed, setCollapsed] = useState(new Set());
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const { activeStoreId } = useStores();

  const load = () => {
    const params = {};
    if (q) params.q = q;
    if (status) params.status = status;
    if (lowStock) params.low_stock = true;
    if (activeStoreId) params.store_id = activeStoreId;
    api.get("/products", { params }).then((r) => setItems(r.data));
  };
  useEffect(() => { load(); }, [q, status, lowStock, activeStoreId]);
  useEffect(() => { setPage(1); }, [q, status, lowStock, activeStoreId]);

  const toggleSel = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };
  const toggleAll = () => {
    if (selected.size === items.length) setSelected(new Set());
    else setSelected(new Set(items.map((p) => p.id)));
  };
  const bulkPrint = () => {
    const picks = items.filter((p) => selected.has(p.id));
    if (picks.length === 0) return toast.error("Select at least one product");
    setBulkPrintItems(picks);
  };

  const toggleCollapse = (name) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const isLow = (p) => p.status === "in_shelf" && p.stock <= (p.low_stock_threshold || 2);

  const statusBadge = (p) => {
    if (p.status === "sold") return { bg: "var(--surface-error)", color: "var(--error)", label: "Sold" };
    if (p.status === "draft") return { bg: "var(--surface-alert)", color: "var(--warning)", label: "Draft" };
    return { bg: "var(--surface-success)", color: "var(--success)", label: "In shelf" };
  };

  const grouped = groupProducts(items);
  const totalProducts = grouped.length;
  const totalVariants = items.length;
  const pageCount = Math.max(1, Math.ceil(totalProducts / perPage));
  const safePage = Math.min(page, pageCount);
  const pagedGroups = grouped.slice((safePage - 1) * perPage, safePage * perPage);

  const openGallery = (name, images, startIndex = 0) => {
    if (!images?.length) return;
    setPreview({ name, images, startIndex });
  };

  const renderThumb = (src, { label, images, startIndex, className = "w-10 h-10" }) => (
    <button
      type="button"
      onClick={() => openGallery(label, images, startIndex)}
      className="shrink-0 rounded-lg border overflow-hidden cursor-zoom-in focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1"
      style={{ borderColor: "var(--border-soft)" }}
      aria-label={`View image for ${label}`}
    >
      <img src={imageUrl(src)} alt="" className={`${className} object-cover`} />
    </button>
  );

  const renderVariantRow = (p) => {
    const s = statusBadge(p);
    const thumb = pickVariantImage(p);
    const galleryImages = Array.isArray(p.images) && p.images.length ? p.images.filter(Boolean) : (thumb ? [thumb] : []);
    const variantLabel = [p.name, p.color, p.size].filter(Boolean).join(" · ");
    return (
      <div
        key={p.id}
        className={`${ROW_GRID} py-3 border-t text-sm`}
        style={{ borderColor: "var(--border-soft)" }}
        data-testid={`product-row-${p.barcode}`}
      >
        <div>
          <input
            type="checkbox"
            data-testid={`product-select-${p.barcode}`}
            checked={selected.has(p.id)}
            onChange={() => toggleSel(p.id)}
          />
        </div>

        <div>
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="font-semibold">{p.size || "—"}</span>
            {isLow(p) && (
              <span
                className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full font-semibold"
                style={{ background: "var(--surface-alert)", color: "var(--warning)" }}
              >
                <AlertTriangle size={10} /> LOW
              </span>
            )}
          </div>
          <div className="text-[10px] uppercase tracking-wide mt-0.5" style={{ color: "var(--text-muted)" }}>
            Size
          </div>
        </div>

        <div>
          {thumb ? (
            renderThumb(thumb, { label: variantLabel, images: galleryImages, startIndex: 0, className: "w-10 h-10" })
          ) : (
            <div className="w-10 h-10 rounded-lg border" style={{ borderColor: "var(--border-soft)", background: "var(--surface-muted)" }} />
          )}
        </div>

        <div className="font-mono text-xs truncate">{p.barcode}</div>
        <div>
          <ColorPill color={(p.color || "").trim() || null} />
        </div>
        <div className="text-right num font-semibold whitespace-nowrap">
          {p.status === "draft" && !p.price ? "—" : rupee(p.price)}
        </div>
        <div className="text-center font-mono text-xs whitespace-nowrap" style={{ color: "var(--text-muted)" }}>
          {p.hsn_code || "—"}
        </div>
        <div className="text-center num text-xs font-semibold whitespace-nowrap">
          {p.gst_percent ?? 5}%
        </div>
        <div className="text-center">
          <span
            className="inline-block min-w-[2rem] px-2 py-1 rounded text-xs font-semibold num"
            style={{ background: "var(--surface-muted)" }}
            title="Units in stock"
          >
            {p.stock}
          </span>
        </div>
        <div>
          <span className="inline-block text-xs px-2.5 py-1 rounded-full whitespace-nowrap" style={{ background: s.bg, color: s.color }}>
            {s.label}
          </span>
        </div>
      </div>
    );
  };

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto" data-testid="products-root">
      <div className="flex items-start justify-between gap-3 mb-6 no-print flex-wrap">
        <div>
          <h1 className="font-display font-bold text-3xl">Products</h1>
          <p className="text-sm" style={{ color: "var(--text-muted)" }}>
            Add items and generate barcodes to print &amp; stick.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            data-testid="products-bulk-import-btn"
            onClick={() => setShowImport(true)}
            className="inline-flex items-center gap-2 px-5 h-12 rounded-md font-semibold border"
            style={{ borderColor: "var(--primary)", color: "var(--primary)" }}
          >
            <Upload size={18} /> Bulk import
          </button>
          <Link
            to="/app/products/new"
            data-testid="products-add-btn"
            className="inline-flex items-center gap-2 px-5 h-12 rounded-md text-white tactile-btn font-semibold"
            style={{ background: "var(--primary)" }}
          >
            <Plus size={18} /> Add Product
          </Link>
        </div>
      </div>

      <div className="card-soft p-4 mb-4 flex flex-wrap items-center gap-3 no-print">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--text-muted)" }} />
          <input
            data-testid="products-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name or barcode..."
            className="h-12 pl-10 pr-3 w-full rounded-md border bg-white"
            style={{ borderColor: "var(--border-soft)" }}
          />
        </div>
        <select
          data-testid="products-status-filter"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="h-12 px-3 rounded-md border bg-white"
          style={{ borderColor: "var(--border-soft)" }}
        >
          <option value="">All status</option>
          <option value="in_shelf">In shelf</option>
          <option value="draft">Draft</option>
          <option value="sold">Sold</option>
        </select>
        <button
          data-testid="products-lowstock-toggle"
          onClick={() => setLowStock((v) => !v)}
          className="h-12 px-4 rounded-md text-sm font-semibold border inline-flex items-center gap-2"
          style={{
            borderColor: lowStock ? "var(--warning)" : "var(--border-soft)",
            background: lowStock ? "var(--surface-alert)" : "white",
            color: lowStock ? "var(--warning)" : "inherit",
          }}
        >
          <AlertTriangle size={16} /> Low stock only
        </button>
        {selected.size > 0 && (
          <button
            data-testid="products-bulk-print-btn"
            onClick={bulkPrint}
            className="h-12 px-5 rounded-md text-white font-semibold tactile-btn inline-flex items-center gap-2"
            style={{ background: "var(--secondary)" }}
          >
            <Printer size={16} /> Print {selected.size} barcode{selected.size > 1 ? "s" : ""}
          </button>
        )}
      </div>

      <div className="no-print min-w-[1000px]">
        {/* Column headers */}
        <div
          className={`${ROW_GRID} py-3 text-xs font-medium uppercase tracking-wide`}
          style={{ color: "var(--text-muted)" }}
        >
          <div>
            <input
              type="checkbox"
              data-testid="products-select-all"
              checked={items.length > 0 && selected.size === items.length}
              onChange={toggleAll}
            />
          </div>
          <div>Size</div>
          <div>Image</div>
          <div>Barcode</div>
          <div>Color</div>
          <div className="text-right">Price</div>
          <div className="text-center">HSN</div>
          <div className="text-center">GST</div>
          <div className="text-center">Qty</div>
          <div>Status</div>
        </div>

        {items.length === 0 && (
          <div className="card-soft text-center py-16 text-sm" style={{ color: "var(--text-muted)" }}>
            No products yet.
          </div>
        )}

        <div className="space-y-4 mt-2">
          {pagedGroups.map((group) => {
            const firstVariantId = group.variants[0]?.id;
            const isCollapsed = collapsed.has(group.name);
            const meta = [group.category, group.fabric, group.shelf].filter(Boolean).join(" · ");

            return (
              <div
                key={group.name}
                className="rounded-xl border bg-white overflow-hidden shadow-sm"
                style={{ borderColor: "var(--border-soft)" }}
              >
                {/* Product header */}
                <div className="flex items-start gap-4 p-4">
                  <div className="shrink-0">
                    {group.image ? (
                      renderThumb(group.image, {
                        label: group.name,
                        images: group.images?.length ? group.images : [group.image],
                        startIndex: 0,
                        className: "w-16 h-16",
                      })
                    ) : (
                      <div
                        className="w-16 h-16 rounded-lg border"
                        style={{ borderColor: "var(--border-soft)", background: "var(--surface-muted)" }}
                      />
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <h2 className="font-display font-bold text-lg leading-tight">{group.name}</h2>
                    <p className="text-sm mt-0.5" style={{ color: "var(--text-muted)" }}>
                      {meta}
                      {meta ? " · " : ""}
                      {group.variantCount} variant{group.variantCount === 1 ? "" : "s"}
                    </p>
                    {(group.sizes.length > 0) && (
                      <div className="flex flex-wrap items-center gap-1.5 mt-2">
                        {group.sizes.map((sz) => (
                          <span
                            key={sz}
                            className="inline-flex items-center text-xs font-medium px-2.5 py-1 rounded-full border"
                            style={{ borderColor: "var(--border-soft)", color: "var(--text-muted)", background: "var(--surface-muted)" }}
                          >
                            {sz}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    {firstVariantId && (
                      <Link
                        to={`/app/products/${firstVariantId}/edit`}
                        data-testid={`product-group-edit-${group.name}`}
                        className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md border text-sm font-medium"
                        style={{ borderColor: "var(--border-soft)" }}
                      >
                        <Pencil size={14} /> Edit product
                      </Link>
                    )}
                    <button
                      type="button"
                      onClick={() => toggleCollapse(group.name)}
                      className="inline-flex items-center justify-center w-9 h-9 rounded-md border"
                      style={{ borderColor: "var(--border-soft)", color: "var(--text-muted)" }}
                      aria-label={isCollapsed ? "Expand variants" : "Collapse variants"}
                    >
                      {isCollapsed ? <ChevronDown size={18} /> : <ChevronUp size={18} />}
                    </button>
                  </div>
                </div>

                {/* Variant rows */}
                {!isCollapsed && group.variants.map((p) => renderVariantRow(p))}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        {totalProducts > 0 && (
          <div
            className="flex flex-wrap items-center justify-between gap-4 mt-6 pt-4 text-sm"
            style={{ color: "var(--text-muted)", borderTop: "1px solid var(--border-soft)" }}
          >
            <p>
              Showing {totalProducts} product{totalProducts === 1 ? "" : "s"}
              {" · "}
              {totalVariants} variant{totalVariants === 1 ? "" : "s"}
            </p>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2">
                <select
                  value={perPage}
                  onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}
                  className="h-9 px-2 rounded-md border bg-white text-sm"
                  style={{ borderColor: "var(--border-soft)" }}
                >
                  <option value={10}>10 per page</option>
                  <option value={25}>25 per page</option>
                  <option value={50}>50 per page</option>
                </select>
              </label>
              <span className="text-sm whitespace-nowrap">
                {safePage} of {pageCount}
              </span>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  disabled={safePage <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="inline-flex items-center justify-center w-9 h-9 rounded-md border disabled:opacity-40"
                  style={{ borderColor: "var(--border-soft)" }}
                >
                  <ChevronLeft size={18} />
                </button>
                <button
                  type="button"
                  disabled={safePage >= pageCount}
                  onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                  className="inline-flex items-center justify-center w-9 h-9 rounded-md border disabled:opacity-40"
                  style={{ borderColor: "var(--border-soft)" }}
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {bulkPrintItems && <BulkBarcodePrintModal items={bulkPrintItems} onClose={() => setBulkPrintItems(null)} />}
      {showImport && (
        <BulkProductImportModal
          storeId={activeStoreId}
          onClose={() => setShowImport(false)}
          onSuccess={load}
        />
      )}

      <ProductImageGalleryDialog
        open={!!preview}
        onOpenChange={(open) => !open && setPreview(null)}
        name={preview?.name}
        images={preview?.images}
        startIndex={preview?.startIndex ?? 0}
      />
    </div>
  );
}
